import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ContactService } from '@app/contacts/shared/contact.service';
import { NotificationService } from '@app/shared/notification/notification.service';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { CheckboxComponent } from '@app/partials/form/checkbox/checkbox.component';
import { SettingService } from '@app/settings/setting.service';
import { SETTING_KEYS } from '@app/settings/setting.model';
import { UniqueValidator } from '@app/shared/validators/unique.validator';
import { AbstractEntityEditComponent } from '@app/shared/abstract/abstract-entity-edit.component';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';

/**
 * TVG-315: shown only until the setting-backed wording arrives from the API.
 * Deliberately marked as a placeholder, like the seeded setting value.
 */
const TEXT_PERMISSION_WORDING_FALLBACK =
  '[PLACEHOLDER WORDING — AWAITING RICHARD BRAY\'S APPROVED COPY] Tick to let us send you text messages about your event.';

@Component({
  selector: 'app-contact-form',
  standalone: true,
  imports: [
    TextInputComponent,
    ButtonComponent,
    CheckboxComponent,
    ReactiveFormsModule,
  ],
  template: `
    <div class="h-full flex flex-col">
      <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Create New Contact</h3>
      </div>
      
      <div class="flex-1 overflow-y-auto p-6">
        <form [formGroup]="form">
          <div class="mb-6">
            <app-text-input 
              [control]="form.controls['name']" 
              [label]="'Name'" 
              [placeholder]="'Contact Name'"
              [required]="true">
            </app-text-input>
          </div>
          
          <div class="mb-6">
            <app-text-input 
              [control]="form.controls['email']" 
              [label]="'Email'" 
              [placeholder]="'email@example.com'"
              [type]="'email'"
              [required]="true">
            </app-text-input>
          </div>
          
          <div class="mb-6">
            <app-text-input
              [control]="form.controls['phone']"
              [label]="'Phone'"
              [placeholder]="'Phone Number'">
            </app-text-input>
          </div>

          <!-- TVG-315: the text permission tick box only appears once a mobile
               number has actually been entered. Its wording comes from the
               \`text-permission-opt-in-wording\` setting so Richard's approved
               copy can replace the placeholder without a code change. -->
          @if (hasMobileNumber()) {
            <div class="mb-6 rounded-lg border border-gray-200 p-4 dark:border-gray-700" data-testid="text-permission-opt-in">
              <app-checkbox
                [control]="form.controls['text_permission']"
                [label]="textPermissionWording"
                [inline]="true">
              </app-checkbox>
            </div>
          }

          <div class="mb-6">
            <app-text-input 
              [control]="form.controls['address_line_1']" 
              [label]="'Address'" 
              [placeholder]="'Address'">
            </app-text-input>
          </div>
          
          <div class="mb-6">
            <app-text-input 
              [control]="form.controls['city']" 
              [label]="'City'" 
              [placeholder]="'City'">
            </app-text-input>
          </div>
          
          <div class="mb-6">
            <app-text-input 
              [control]="form.controls['postcode']" 
              [label]="'Postcode'" 
              [placeholder]="'Postcode'">
            </app-text-input>
          </div>
          
          <div class="mb-6">
            <app-text-input 
              [control]="form.controls['country']" 
              [label]="'Country'" 
              [placeholder]="'Country'">
            </app-text-input>
          </div>
        </form>
      </div>
      
      <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <div class="flex gap-3">
          <app-button 
            [label]="'Cancel'" 
            [style]="'secondary'" 
            [disabled]="processing"
            (click)="cancel()">
          </app-button>
          <app-button 
            [label]="'Create Contact'" 
            [disabled]="processing || !form.valid"
            (click)="save()">
          </app-button>
        </div>
      </div>
    </div>
  `
})
export class ContactFormComponent implements OnDestroy, OnInit {
  @Output() contactCreated = new EventEmitter<{id: number, name: string}>();
  @Output() cancelled = new EventEmitter<void>();

  processing = false;

  /**
   * TVG-315: wording beside the text permission tick box. Loaded from the
   * `text-permission-opt-in-wording` setting; the fallback only shows if the
   * settings request has not landed, and is marked as a placeholder for the
   * same reason the seeded value is — Richard's approved copy is still to come.
   */
  textPermissionWording = TEXT_PERMISSION_WORDING_FALLBACK;

  private destroy$ = new Subject<void>();

  form = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.minLength(3)]),
    email: new FormControl('', [Validators.required, Validators.email]),
    phone: new FormControl(''),
    address_line_1: new FormControl(''),
    city: new FormControl(''),
    postcode: new FormControl(''),
    country: new FormControl(''),
    contact_status: new FormControl('Opportunity'),
    phone_numbers: new FormControl<string[]>([]),
    // TVG-315: text permission is on by default, so the tick box starts ticked
    // and the customer unticks to opt out.
    text_permission: new FormControl<boolean>(true, { nonNullable: true }),
  });

  constructor(
    private contactService: ContactService,
    private notificationService: NotificationService,
    private uniqueValidator: UniqueValidator,
    private settingService: SettingService
  ) {
    // Add async validators after form is created
    this.form.controls['email'].addAsyncValidators([this.uniqueValidator.uniqueEmail()]);
  }

  ngOnInit(): void {
    this.settingService
      .value$(SETTING_KEYS.textPermissionOptInWording)
      .pipe(takeUntil(this.destroy$))
      .subscribe((wording) => {
        this.textPermissionWording = wording?.trim() || TEXT_PERMISSION_WORDING_FALLBACK;
      });

    this.settingService.loadPublic().subscribe({ error: () => undefined });
  }

  /**
   * TVG-315: the tick box only appears once a number has been typed, which is
   * the "where a customer adds a mobile number" trigger Mark asked for.
   */
  hasMobileNumber(): boolean {
    return (this.form.controls['phone'].value ?? '').trim().length > 0;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  save() {
    if (this.form.invalid) return;

    this.processing = true;
    
    // Generate a random password temporarily
    const tempPassword = Math.random().toString(36).substring(2, 15);
    
    const formData = {
      ...this.form.value,
      password: tempPassword,
      phone_numbers: this.form.value.phone ? [this.form.value.phone] : [],
      // TVG-315: only send a text permission the user was actually shown.
      // Without a number there is no tick box, so the backend default stands.
      text_permission: this.hasMobileNumber() ? this.form.value.text_permission : undefined,
    };

    this.contactService.save(formData as any).subscribe({
      next: (response: any) => {
        this.notificationService.success('Contact created successfully');
        this.contactCreated.emit({
          id: response.data.id,
          name: response.data.name
        });
        this.form.reset();
        this.processing = false;
      },
      error: (res: any) => {
        if (res.status === 422) {
          AbstractEntityEditComponent.bindErrorsToForm(res.error, this.form);
        }
        this.notificationService.error('Failed to create contact');
        this.processing = false;
      }
    });
  }

  cancel() {
    this.form.reset();
    this.cancelled.emit();
  }
}
